import { createSlice, Slice } from '@reduxjs/toolkit';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import {
	formateApiDateValue,
	formateNullableEnumValue,
} from 'modules/utilities/Helpers';
import { RootStateOrAny } from 'react-redux';
import { MailsTemplatesResponse } from '../interfaces/emailTemplateVersion';
import { IMailDetails, IMailListItem } from '../interfaces/mail';

export const NOTIFICATION_KEY_IN_STORE = 'mails';

export interface IMailsState {
	isLoading: boolean;
	mails: IPaginatedData<IMailListItem>;
	deleteMail: IReqStatus;
	addMail: IReqStatus;
	mailsTemplates: {
		data: MailsTemplatesResponse;
		isLoading: boolean;
	};
	updateMail: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IMailDetails;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
}

const initialState: IMailsState = {
	mails: {
		items: [],
		meta: {
			currentPage: 1,
			itemsPerPage: 5,
			itemCount: 2,
			totalItems: 2,
			totalPages: 1,
		},
	},
	mailsTemplates: {
		isLoading: false,
		data: {
			result: [],
			_metadata: {
				count: 0,
				self: '',
			},
		},
	},
	isLoading: false,
	addMail: {
		success: false,
		error: false,
	},
	deleteMail: {
		success: false,
		error: false,
	},
	updateMail: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
};

const mailSlice: Slice = createSlice({
	name: NOTIFICATION_KEY_IN_STORE,
	initialState,
	reducers: {
		getAllMails: state => {
			state.isLoading = true;
		},
		getAllMailsSuccess: (state, action) => {
			const { items, meta } = action.payload;
			state.mails = {
				items: items.map((item: IMailListItem) => ({
					...item,
					scheduledAt: formateApiDateValue(item.scheduledAt),
				})),
				meta,
			};
			state.isLoading = false;
		},
		getAllMailsFailure: state => {
			state.isLoading = false;
		},
		deleteMail: state => {
			state.deleteMail = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteMailSuccess: (state: RootStateOrAny) => {
			state.deleteMail = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteMailFailure: (state: RootStateOrAny) => {
			state.deleteMail = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		resetDeleteMailStatus: state => {
			state.deleteMail = {
				success: false,
				error: false,
			};
		},
		addMail: state => {
			state.isLoading = true;
			state.addMail = {
				success: false,
				error: false,
			};
		},
		addMailSuccess: (state: RootStateOrAny) => {
			state.addMail = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		addMailFailure: state => {
			state.addMail = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		getMailToUpdate: state => {
			state.updateMail.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getMailToUpdateSuccess: (state, action) => {
			state.updateMail.itemToUpdate = {
				isLoading: false,
				data: {
					...action.payload,
					scheduledAt: formateApiDateValue(action.payload.scheduledAt),
					endUsersFilter: {
						...action.payload.endUsersFilter,
						currentRegion: formateNullableEnumValue(
							action.payload.endUsersFilter.currentRegion,
						),
						gender: formateNullableEnumValue(
							action.payload.endUsersFilter.gender,
						),
					},
				},
				status: {
					success: true,
					error: false,
				},
			};
		},
		getMailToUpdateFailure: state => {
			state.updateMail.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateMail: state => {
			state.isLoading = true;
			state.updateMail.status = {
				success: false,
				error: false,
			};
		},
		updateMailSuccess: state => {
			state.isLoading = false;
			state.updateMail.status = {
				success: true,
				error: false,
			};
		},
		updateMailFailure: state => {
			state.isLoading = false;
			state.updateMail.status = {
				success: false,
				error: true,
			};
		},

		getMailsTemplates: state => {
			state.mailsTemplates = {
				...state.mailsTemplates,
				isLoading: true,
			};
		},

		getMailsTemplatesSuccess: (state, action) => {
			const payload: MailsTemplatesResponse = action.payload;
			const isNewList = !payload._metadata.prev;
			state.mailsTemplates = {
				data: {
					...payload,
					result: [
						...(isNewList ? [] : state.mailsTemplates.data.result),
						...payload.result,
					],
				},
				isLoading: false,
			};
		},

		getMailsTemplatesFailure: (state, action) => {
			state.mailsTemplates = {
				...state.mailsTemplates,
				isLoading: false,
			};
		},

		resetMailData: () => {
			return initialState;
		},
	},
});

export const {
	getAllMails,
	getAllMailsSuccess,
	getAllMailsFailure,

	deleteMail,
	deleteMailSuccess,
	deleteMailFailure,

	resetDeleteMailStatus,
	addMail,
	addMailSuccess,
	addMailFailure,
	resetMailData,

	getMailToUpdate,
	getMailToUpdateSuccess,
	getMailToUpdateFailure,

	updateMail,
	updateMailSuccess,
	updateMailFailure,

	getMailsTemplates,
	getMailsTemplatesSuccess,
	getMailsTemplatesFailure,
} = mailSlice.actions;

export const mailStateSelector = (state: RootStateOrAny): IMailsState =>
	state[NOTIFICATION_KEY_IN_STORE];

export default mailSlice;
